/**
 * DEV-ONLY role preview switcher. Lets you see the dashboard as any role/scope shape without
 * needing a real Entra account per persona.
 *
 * Renders only when `MOCK_ENABLED` (a Vite dev build AND `VITE_BYPASS_AUTH=true`). In a production
 * build `import.meta.env.DEV` is the constant `false`, so the `MOCK_ENABLED &&` guard at the call
 * site in App.tsx folds away and Rollup tree-shakes this module out — same mechanism that keeps
 * dev/mock.ts out of prod. This is NOT server-side impersonation (removed in the 2026-07-08 SSO
 * cutover): it only rewrites the client-side `ait_user` that the mock API layer scopes off, so it
 * has no effect against a real API.
 *
 * Selecting a persona writes `ait_user` and does a full page load of /dashboard, so every query
 * refetches under the new scope instead of leaving stale admin-scoped data on screen.
 */
import React, {useState} from 'react';
import type {SessionUser} from '../lib/api';
import {DEMO_USER, ROLE_PREVIEWS} from './personas';

const SESSION_KEY = 'ait_user';

interface Persona {
  id: string;
  label: string;
  user: SessionUser;
}

const PERSONAS: Persona[] = [
  {id: 'admin', label: 'Admin · all portfolios', user: DEMO_USER},
  ...ROLE_PREVIEWS.map(p => ({id: p.id, label: p.label, user: p.user}))
];

function currentEmail(): string | null {
  const raw = sessionStorage.getItem(SESSION_KEY);
  return raw ? ((JSON.parse(raw) as SessionUser).email ?? null) : null;
}

/** Persist the persona as the signed-in user, then hard-navigate so all data refetches. */
function previewAs(user: SessionUser): void {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(user));
  window.location.assign('/dashboard');
}

function signOut(): void {
  sessionStorage.removeItem(SESSION_KEY);
  window.location.assign('/');
}

export default function RolePreviewSwitcher(): React.ReactElement {
  const [open, setOpen] = useState(false);
  const email = currentEmail();
  const active = PERSONAS.find(p => p.user.email === email);

  return (
    <div className="fixed bottom-4 left-4 z-[9999] font-sans">
      {open && (
        <div className="mb-2 w-80 rounded-lg border border-amber-300 bg-white shadow-xl overflow-hidden">
          <div className="px-3 py-2 bg-amber-50 border-b border-amber-200">
            <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide">View as</p>
            <p className="text-[11px] text-amber-700">Local dev only · client-side scope preview</p>
          </div>
          <ul className="max-h-80 overflow-y-auto divide-y divide-gray-100">
            {PERSONAS.map(p => {
              const isActive = p.user.email === email;
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => previewAs(p.user)}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${
                      isActive ? 'bg-blue-50 font-semibold text-blue-800' : 'text-gray-700'
                    }`}
                  >
                    <span className="block">{p.label}</span>
                    <span className="block text-[11px] text-gray-400 font-normal">{p.user.email}</span>
                  </button>
                </li>
              );
            })}
          </ul>
          {email && (
            <button
              type="button"
              onClick={signOut}
              className="w-full px-3 py-2 text-xs text-gray-500 hover:bg-gray-50 border-t border-gray-100"
            >
              Sign out
            </button>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="px-3 py-1.5 rounded-full bg-amber-400 text-amber-950 text-xs font-semibold shadow-lg hover:bg-amber-300"
      >
        VIEW AS: {active ? active.label : 'signed out'}
      </button>
    </div>
  );
}
